import type { NearbyPlace } from '@/lib/nearest-place';
import { foldPlace } from '@/lib/provinces';

/** The same city however it is written: Montréal, Montreal, montreal. */
export const sameCity = (a: { name?: string; city?: string; province?: string }, b: { name?: string; city?: string; province?: string }) =>
  a.province === b.province && foldPlace(a.name ?? a.city ?? '') === foldPlace(b.name ?? b.city ?? '');

// The cities near a visitor, kept for the tab only (sessionStorage: gone when the tab is closed, never sent anywhere), so that the page of
// the nearest city can say "also near you" and let the person widen the circle without a second request for their position. It holds city
// names and distances rounded to 5 km, not coordinates.

export const NEARBY_KEY = 'rec:nearby:v1';
export const RADII = [25, 50, 100] as const;
export type Radius = (typeof RADII)[number];

export function parseNearby(raw: string | null | undefined): NearbyPlace[] {
  try {
    const v = JSON.parse(raw || '[]');
    if (!Array.isArray(v)) return [];
    return v
      .filter((p) => p && typeof p.name === 'string' && p.name.length < 80 && typeof p.province === 'string' && /^[A-Z]{2}$/.test(p.province) && typeof p.km === 'number' && p.km >= 0 && p.km <= 500)
      .slice(0, 20)
      .map((p) => ({ name: p.name, province: p.province, km: p.km }));
  } catch {
    return [];
  }
}

/** The cities within the radius, without the one the person is already looking at. */
export function nearbyWithin(list: NearbyPlace[], radius: number, here?: { city?: string; province?: string }): NearbyPlace[] {
  return list.filter((p) => p.km <= radius && !(here && sameCity(p, here))).slice(0, 8);
}

export function saveNearby(list: NearbyPlace[]) {
  try {
    sessionStorage.setItem(NEARBY_KEY, JSON.stringify(list));
  } catch {
    // storage off: the chips do not appear, nothing else changes
  }
}

export function readNearby(): NearbyPlace[] {
  try {
    return parseNearby(sessionStorage.getItem(NEARBY_KEY));
  } catch {
    return [];
  }
}

// What the button does on a press, before it asks the browser for anything.
export type GeoAction = 'explain' | 'locate' | 'blocked';

/**
 * First press: say what will happen and why (the browser's own question follows). Later presses go straight to the browser, which
 * remembers its own answer. A position the browser has been told never to share is not asked for again: the person is told how to change
 * it, or to choose a city.
 */
export function geoAction(permission: 'granted' | 'denied' | 'prompt' | 'unknown', explained: boolean): GeoAction {
  if (permission === 'denied') return 'blocked';
  if (permission === 'granted') return 'locate';
  return explained ? 'locate' : 'explain';
}
